import { inject, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, FormGroup } from '@angular/forms';
import { FilteringService } from '../services/filtering.service';
import { PagerBase } from './pagerBase';
import { SnackMessageService } from '../services/snack-message.service';

export interface IBaseListViewState {
  page: PageEvent;
  sort: Sort | null;
}
//this auto generates form group interface based in values interface to make sure that same values are existing in both interfaces
export type IFilterControls<T> = {
  [K in keyof T]: FormControl<T[K]>;
};

export abstract class BaseListStateService<TItem, TParams extends string, TFilterValues> extends PagerBase {
  protected defaultSort: Sort | null = null;
  protected defaultPaging: PageEvent = { pageIndex: 0, pageSize: this.defaultPageSize, length: 0 };
  readonly viewState: WritableSignal<IBaseListViewState>;
  readonly loading = signal<boolean>(false);
  protected filteringService = inject(FilteringService);
  protected activatedRoute = inject(ActivatedRoute);

  public snackMessageService = inject(SnackMessageService);
  protected readonly queryParamMap$ = toSignal(this.activatedRoute.queryParamMap);
  protected router = inject(Router);
  readonly pagingOptions = signal<number[]>(this.pageSizes);
  protected refreshTrigger = signal(0);

  protected abstract createQueryParams(filters: Partial<TFilterValues>): Record<TParams, string | null>;
  protected abstract form: FormGroup<IFilterControls<TFilterValues>>;

  constructor(defaultSort: Sort | null = null) {
    super();
    this.defaultSort = defaultSort;
    this.viewState = signal<IBaseListViewState>({
      page: this.defaultPaging,
      sort: defaultSort,
    });
  }

  triggerRefresh(): void {
    this.refreshTrigger.update(x => x + 1);
  }

  updatePage(pageEvent: PageEvent): void {
    this.viewState.update(current => ({
      ...current,
      page: pageEvent,
    }));
  }

  updateSort(sort: Sort): void {
    this.viewState.update(current => ({
      ...current,
      sort,
      page: { ...current.page, pageIndex: 0 },
    }));
  }

  //when we change filters AND want to skip to first page in one go
  ToFirstPage(formValues?: Partial<TFilterValues>): void {
    if (formValues) {
      this.form.patchValue(formValues as any, { emitEvent: true });
    }
    this.viewState.update(current => ({
      ...current,
      page: { ...current.page, pageIndex: 0 },
    }));
  }

  // Must be called once from the subclass constructor, after `form` has been assigned, so that ANY
  // filter change (including controls bound directly via formControlName) resets back to page 1.
  protected resetPageOnFilterChange(): void {
    this.form.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      this.viewState.update(current => ({
        ...current,
        page: { ...current.page, pageIndex: 0 },
      }));
    });
  }
}
